import { useState } from "react";
import axios from "axios";
import { API } from "../../../App";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency, formatDate, SourceBadge } from "./accountingUtils";

const JournalEntries = ({ accounts, journalEntries, onRefresh }) => {
  const [showNewEntry, setShowNewEntry] = useState(false);
  const [newEntry, setNewEntry] = useState({
    entry_date: new Date().toISOString().split("T")[0],
    description: "",
    lines: [
      { account_id: "", debit: 0, credit: 0 },
      { account_id: "", debit: 0, credit: 0 },
    ],
  });

  const totalDebit = newEntry.lines.reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0);
  const totalCredit = newEntry.lines.reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0);

  const addEntryLine = () => {
    setNewEntry({ ...newEntry, lines: [...newEntry.lines, { account_id: "", debit: 0, credit: 0 }] });
  };

  const updateEntryLine = (index, field, value) => {
    const lines = [...newEntry.lines];
    lines[index] = { ...lines[index], [field]: value };
    setNewEntry({ ...newEntry, lines });
  };

  const handleCreateEntry = async (e) => {
    e.preventDefault();
    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      toast.error(`Entry not balanced. Debit: ${totalDebit}, Credit: ${totalCredit}`);
      return;
    }
    try {
      await axios.post(`${API}/accounting/journal-entries`, newEntry);
      toast.success("Journal entry created");
      setShowNewEntry(false);
      setNewEntry({
        entry_date: new Date().toISOString().split("T")[0],
        description: "",
        lines: [
          { account_id: "", debit: 0, credit: 0 },
          { account_id: "", debit: 0, credit: 0 },
        ],
      });
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create entry");
    }
  };

  return (
    <div data-testid="journal-entries">
      <div className="flex justify-end mb-3">
        <Button onClick={() => setShowNewEntry(true)} data-testid="new-journal-entry-btn">
          <Plus className="w-4 h-4 mr-2" />
          Journal Entry
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Journal Entries</CardTitle>
        </CardHeader>
        <CardContent>
          {journalEntries.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No journal entries yet. Click &quot;Journal Entry&quot; to create one.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Entry #</th>
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Description</th>
                    <th className="text-left p-2">Source</th>
                    <th className="text-right p-2">Debit</th>
                    <th className="text-right p-2">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {journalEntries.map((entry) => (
                    <tr key={entry.entry_id} className="border-b last:border-0">
                      <td className="p-2 font-mono">{entry.entry_number}</td>
                      <td className="p-2">{formatDate(entry.entry_date)}</td>
                      <td className="p-2">{entry.description}</td>
                      <td className="p-2"><SourceBadge entry={entry} /></td>
                      <td className="p-2 text-right">{formatCurrency(entry.total_debit)}</td>
                      <td className="p-2 text-right">{formatCurrency(entry.total_credit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {showNewEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>New Journal Entry</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateEntry} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Date *</label>
                    <Input
                      type="date"
                      value={newEntry.entry_date}
                      onChange={(e) => setNewEntry({ ...newEntry, entry_date: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description *</label>
                  <Input
                    value={newEntry.description}
                    onChange={(e) => setNewEntry({ ...newEntry, description: e.target.value })}
                    placeholder="Entry description"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Entry Lines</label>
                  <div className="space-y-2">
                    {newEntry.lines.map((line, index) => (
                      <div key={index} className="grid grid-cols-3 gap-2">
                        <select
                          value={line.account_id}
                          onChange={(e) => updateEntryLine(index, "account_id", e.target.value)}
                          className="px-2 py-1 border rounded-md text-sm"
                          required
                        >
                          <option value="">Select Account</option>
                          {accounts.map((acc) => (
                            <option key={acc.account_id} value={acc.account_id}>
                              {acc.account_code} - {acc.account_name}
                            </option>
                          ))}
                        </select>
                        <Input
                          type="number"
                          placeholder="Debit"
                          value={line.debit || ""}
                          onChange={(e) => updateEntryLine(index, "debit", parseFloat(e.target.value) || 0)}
                        />
                        <Input
                          type="number"
                          placeholder="Credit"
                          value={line.credit || ""}
                          onChange={(e) => updateEntryLine(index, "credit", parseFloat(e.target.value) || 0)}
                        />
                      </div>
                    ))}
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addEntryLine} className="mt-2">
                    <Plus className="w-4 h-4 mr-1" /> Add Line
                  </Button>
                </div>
                <div className="bg-gray-50 p-3 rounded-md">
                  <div className="flex justify-between text-sm">
                    <span>Total Debit:</span>
                    <span className="font-bold">{formatCurrency(totalDebit)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Total Credit:</span>
                    <span className="font-bold">{formatCurrency(totalCredit)}</span>
                  </div>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowNewEntry(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1">Create Entry</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default JournalEntries;
